// creator-portal/src/pages/Login.tsx
// ✅ UPDATED: Handles redirect message from registration page

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Auth.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [info, setInfo] = useState(''); // ✅ NEW: Info message from redirect
  const [loading, setLoading] = useState(false);

  // ✅ Check for redirect message from registration
  useEffect(() => {
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setInfo(state.message);
      // Clear the state so message doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error
        || err.response?.data?.message
        || err.message
        || 'Login failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f8f8]">
      <div 
        className="bg-white flex flex-col items-center relative shadow-[0px_4px_30px_rgba(0,0,0,0.15)]"
        style={{
          width: '460px',
          paddingBottom: '40px',
          borderRadius: '16px',
          border: '1px solid #222222',
        }}
      >
        {/* Brand Title */}
        <h1 
          className="text-[#131313] leading-none"
          style={{ 
            fontFamily: 'Maxwell, sans-serif', 
            fontSize: '56px', 
            marginTop: '45px' 
          }}
        >
          VASTAVIK
        </h1>

        {/* Welcome Back Text */}
        <h2 
          className="text-black font-bold leading-none"
          style={{ 
            fontFamily: '"Caviar Dreams", sans-serif', 
            fontSize: '32px', 
            marginTop: '20px' 
          }}
        >
          Welcome Back
        </h2>

        {/* Login Heading */}
        <h3 
          className="text-black font-bold leading-none"
          style={{ 
            fontFamily: '"Product Sans", sans-serif', 
            fontSize: '24px', 
            marginTop: '30px' 
          }}
        >
          Login
        </h3>

        <form onSubmit={handleSubmit} className="w-full px-[36px] mt-8 flex flex-col gap-[16px]">
          
          {/* ✅ Info message (from registration redirect) */}
          {info && (
            <div 
              className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-600 text-sm px-4 py-3"
              style={{ fontFamily: '"Product Sans", sans-serif' }}
            >
              {info}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div 
              className="rounded-lg bg-rose-50 border border-rose-200 text-rose-600 text-sm px-4 py-3"
              style={{ fontFamily: '"Product Sans", sans-serif' }}
            >
              {error}
            </div>
          )}

          {/* Email */}
          <div className="flex flex-col">
            <label 
              className="text-black mb-1.5"
              style={{ 
                fontFamily: '"Product Sans", sans-serif', 
                fontSize: '16px', 
                fontWeight: 500 
              }}
            >
              Email
            </label>
            <input
              type="email"
              autoComplete="username"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full h-[48px] border border-[#222222] rounded-[8px] px-4 text-black focus:outline-none focus:ring-1 focus:ring-black transition"
              style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '16px' }}
            />
          </div>

          {/* Password */}
          <div className="flex flex-col">
            <label 
              className="text-black mb-1.5"
              style={{ 
                fontFamily: '"Product Sans", sans-serif', 
                fontSize: '16px', 
                fontWeight: 500 
              }}
            >
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              className="w-full h-[48px] border border-[#222222] rounded-[8px] px-4 text-black focus:outline-none focus:ring-1 focus:ring-black transition"
              style={{ fontFamily: '"Product Sans", sans-serif', fontSize: '16px' }}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-[48px] bg-black text-white rounded-[8px] mt-2 hover:bg-[#222] transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              fontFamily: '"Product Sans", sans-serif', 
              fontSize: '18px',
              fontWeight: 500 
            }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>

          {/* Create Account Link */}
          <div 
            className="text-center mt-4 text-[#333333]"
            style={{ 
              fontFamily: '"Product Sans", sans-serif', 
              fontSize: '14px' 
            }}
          >
            Don't have an account?{' '}
            <Link 
              to="/register" 
              className="text-[#047857] hover:underline"
              style={{ textDecorationColor: '#047857', textUnderlineOffset: '2px' }}
            >
              Create Account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;